import type { IncomingMessage } from "node:http";
import type { NextPageContext } from "next";
import { SessionProvider } from "next-auth/react";
import React from "react";
import CacheProvider from "react-inlinesvg/provider";

import { WebPushProvider } from "@calcom/web/modules/notifications/components/WebPushContext";
import { trpc } from "@calcom/trpc/react";

import type { AppProps } from "@lib/app-providers";

import "../styles/globals.css";

// Auto-recover from ChunkLoadError: when Next.js rebuilds on deploy, old chunk
// hashes no longer exist on the server. Any user with cached HTML who triggers a
// lazy-loaded route will get a 404 for the old chunk. Detect this and hard-reload
// so the browser fetches fresh HTML with the new chunk references.
if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    const isChunkError =
      event.message?.includes("ChunkLoadError") ||
      event.message?.includes("Loading chunk") ||
      event.error?.name === "ChunkLoadError";
    if (isChunkError) {
      // Prevent duplicate reload loops by setting a flag in sessionStorage
      const reloadKey = "chunk_error_reloaded";
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, "1");
        window.location.reload();
      }
    }
  });
  // Clear the reload guard after a successful page load
  window.addEventListener("load", () => {
    sessionStorage.removeItem("chunk_error_reloaded");
  });
}

function MyApp(props: AppProps) {
  const { Component, pageProps } = props;

  return (
    <SessionProvider session={pageProps.session ?? undefined}>
      <WebPushProvider>
        {/* @ts-expect-error FIXME remove this comment when upgrading typescript to v5 */}
        <CacheProvider>
          {Component.PageWrapper ? <Component.PageWrapper {...props} /> : <Component {...pageProps} />}
        </CacheProvider>
      </WebPushProvider>
    </SessionProvider>
  );
}

declare global {
  interface Window {
    calNewLocale: string;
  }
}

MyApp.getInitialProps = async ({ ctx }: { ctx: NextPageContext }) => {
  const { req } = ctx;

  let newLocale = "en";

  if (req) {
    const { getLocale } = await import("@calcom/features/auth/lib/getLocale");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newLocale = await getLocale(req as IncomingMessage & { cookies: Record<string, any> });
  } else if (typeof window !== "undefined" && window.calNewLocale) {
    newLocale = window.calNewLocale;
  }

  return {
    pageProps: {
      newLocale,
    },
  };
};

const WrappedMyApp = trpc.withTRPC(MyApp);

export default WrappedMyApp;
